import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

interface DashboardCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  to: string;
  color: string;
  resource: string;
  action?: string;
}

const DashboardCard: React.FC<DashboardCardProps> = ({ title, description, icon, to, color, resource, action = 'read' }) => {
  const { hasPermission, isAdmin } = useContext(AuthContext);
  
  // Kullanıcının bu karta erişim izni olup olmadığını kontrol et
  if (!isAdmin && !hasPermission(resource, action)) {
    return null; // İzin yoksa kartı gösterme
  }
  
  return (
    <Link 
      to={to}
      className={`group block p-6 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 ${color} overflow-hidden relative`}
    >
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white bg-opacity-10 transition-transform group-hover:scale-150"></div>
      <div className="absolute right-2 bottom-2 opacity-20 group-hover:opacity-30 group-hover:scale-110 transition-all transform rotate-12">
        {icon}
      </div>
      <div className="relative z-10">
        <h3 className="text-xl font-bold mb-2 text-white group-hover:translate-x-1 transition-transform">{title}</h3>
        <p className="text-white text-opacity-90 group-hover:text-opacity-100 text-sm">{description}</p>
        <div className="mt-4 flex items-center text-white text-opacity-90 text-sm font-medium">
          <span>Görüntüle</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </Link>
  );
};

const Dashboard: React.FC = () => {
  const { user } = useContext(AuthContext);
  
  return (
    <div className="container mx-auto px-4 py-6">
      {/* Karşılama Bölümü */}
      <div className="mb-10 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl shadow-lg p-6 md:p-12">
        <div className="max-w-3xl">
          <h1 className="text-4xl font-bold text-white mb-2">
            Merhaba, {user?.username || 'Hoş Geldiniz'}!
          </h1>
          <p className="text-blue-100 text-lg mb-6">
            Tekstil yönetim sisteminize hoş geldiniz. Bu panel üzerinden tüm yönetim işlemlerinizi gerçekleştirebilirsiniz.
          </p>
          <Link 
            to="/barcode-generator" 
            className="inline-flex items-center px-6 py-3 bg-white rounded-lg text-blue-700 font-medium shadow-md hover:bg-blue-50 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1v-2a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
            Barkod Oluşturucu
          </Link>
        </div>
      </div>

      {/* Modül Kartları */}
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Modüller</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {/* Tasarım Modülü */}
        <DashboardCard
          title="Renkler"
          description="Renk paletleri ve renk yönetimi"
          to="/colors"
          color="bg-gradient-to-br from-pink-500 to-rose-600"
          resource="colors"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
          }
        />
        
        <DashboardCard
          title="Kumaşlar"
          description="Kumaş türleri ve özelliklerinin yönetimi"
          to="/fabrics"
          color="bg-gradient-to-br from-blue-500 to-cyan-600"
          resource="fabrics"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
          }
        />

        <DashboardCard
          title="Aksesuarlar"
          description="Düğmeler, fermuar ve aksesuarların yönetimi"
          to="/accessories"
          color="bg-gradient-to-br from-amber-500 to-yellow-600"
          resource="accessories"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
        />

        <DashboardCard
          title="Üreticiler"
          description="Tedarikçi yönetimi ve takibi"
          to="/vendors"
          color="bg-gradient-to-br from-purple-500 to-indigo-600"
          resource="vendors"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
        />

        <DashboardCard
          title="Ürünler"
          description="Ürün katalog ve bilgi yönetimi"
          to="/products"
          color="bg-gradient-to-br from-green-500 to-emerald-600"
          resource="products"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          }
        />

        <DashboardCard
          title="Siparişler"
          description="Sipariş takibi ve yönetimi"
          to="/orders"
          color="bg-gradient-to-br from-red-500 to-orange-600"
          resource="orders"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          }
        />

        <DashboardCard
          title="Kalıp Yönetimi"
          description="Kalıpların tasarımı ve yönetimi"
          to="/modelhane/kalip"
          color="bg-gradient-to-br from-violet-500 to-purple-600"
          resource="modelist"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          }
        />

        <DashboardCard
          title="Üretim Planlaması"
          description="Üretim süreç ve terminlerinin planlaması"
          to="/planlama/uretim"
          color="bg-gradient-to-br from-gray-700 to-gray-900"
          resource="planlama"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          }
        />

        {/* Admin kartları - sadece admin görebilir */}
        <DashboardCard
          title="Kullanıcı Yönetimi"
          description="Kullanıcıların ve izinlerinin yönetimi"
          to="/manage-users"
          color="bg-gradient-to-br from-indigo-700 to-blue-900"
          resource="admin"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          }
        />

        <DashboardCard
          title="Rol Yönetimi"
          description="Yetki ve rol konfigürasyonu"
          to="/manage-roles"
          color="bg-gradient-to-br from-blue-900 to-indigo-900"
          resource="admin"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
        />
      </div>
      
      {/* Hızlı Erişim Bölümü */}
      <h2 className="text-2xl font-bold text-gray-800 mt-12 mb-6">Hızlı Erişim</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Link to="/print-barcodes" className="flex items-center bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <div className="bg-blue-100 rounded-full p-3 mr-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Barkod Yazdırma</h3>
            <p className="text-sm text-gray-500">Ürün barkodlarını yazdırın</p>
          </div>
        </Link>
        
        <Link to="/barcode-generator" className="flex items-center bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <div className="bg-green-100 rounded-full p-3 mr-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1v-2a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Barkod Oluşturucu</h3>
            <p className="text-sm text-gray-500">Özel barkodlar oluşturun</p>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
